import { BaseResource } from '@apis/Resources/model';
import { getTagAutomationGetTagAutomationRules } from '@apis/TagManager';
import { inject, injectable } from 'tsyringe';
import { Platform } from '../PlatformService';
import { queryBuilder } from '../QueryExpr';
import { ResourceService } from './ResourceService';
import { TagAutomationRuleReader } from './TagAutomationRuleReader';
import { TagPolicyService } from './TagPolicyService';

type RawRestrictedTagStats = ReturnType<Awaited<ReturnType<RestrictedTagStatsService['getRawStats']>>['stats']['get']>;
export interface IRestrictedTagStats {
    platforms: Set<Platform>;
    getStats(tag: string): RawRestrictedTagStats | undefined;
    reloadStats(tags: string[]): Promise<IRestrictedTagStats>;
}
class RestrictedTagStats implements IRestrictedTagStats {
    private readonly rawStats = new Map<string, RawRestrictedTagStats>();
    public readonly platforms = new Set<Platform>();

    public constructor(private readonly statsSvc: RestrictedTagStatsService) {}

    public update(rawStats: Awaited<ReturnType<RestrictedTagStatsService['getRawStats']>>) {
        for (const platform of rawStats.platforms) {
            this.platforms.add(platform);
        }
        for (const [key, value] of rawStats.stats.entries()) {
            this.rawStats.set(key, value);
        }
        return this;
    }

    public reloadStats = async (tags: string[]) => {
        const rawStats = await this.statsSvc.getRawStats(tags);
        this.update(rawStats);
        return this;
    };

    public getStats = (tag: string) => {
        return this.rawStats.get(tag);
    };
}
@injectable()
export class RestrictedTagStatsService {
    public constructor(
        @inject(ResourceService) private readonly resourceSvc: ResourceService,
        @inject(TagPolicyService) private readonly tagPolicySvc: TagPolicyService,
        @inject(TagAutomationRuleReader) private readonly tagRuleReader: TagAutomationRuleReader
    ) {}

    public async createStats(tags: string[]) {
        const rawStats = !tags?.length ? { stats: new Map(), platforms: [] } : await this.getRawStats(tags);
        return new RestrictedTagStats(this).update(rawStats);
    }

    public async getRawStats(tags: string[]) {
        const uniqueTags = [...new Set<string>(tags)];
        const [res, pol, aut] = await Promise.all([
            this.getResourceStats(uniqueTags),
            this.getPolicyStats(uniqueTags),
            this.getAutomationStats(uniqueTags),
        ]);

        const stats = uniqueTags.reduce((result, t) => {
            return result.set(t, {
                rules: aut.get(t)?.rules ?? 0,
                policies: pol.platforms.reduce((r, p) => ({ ...r, [p]: pol.counts.get(t)?.[p]?.policies ?? 0 }), {}),
                resources: res[t] ?? 0,
            });
        }, new Map<string, { rules: number; policies: Partial<Record<Platform, number>>; resources: number }>());

        return { stats, platforms: pol.platforms };
    }

    private async safelyGetStats<T>(callback: () => Promise<T>, defaultValue: T) {
        try {
            return await callback();
        } catch (err) {
            console.error(err);
            return defaultValue;
        }
    }

    private getResourceStats = (tags: string[]) =>
        this.safelyGetStats(async () => {
            const result = await queryBuilder<BaseResource>()
                .select((b) =>
                    tags.reduce(
                        (r, t) => ({ ...r, [t]: b.countIf(b.model[`CsTags.${t}`].isNotNull()) as unknown as number }),
                        {} as Record<string, number>
                    )
                )
                .execute((q) => this.resourceSvc.query(q));

            return result?.Results?.[0] ?? {};
        }, {});

    private getPolicyStats = (tags: string[]) =>
        this.safelyGetStats(
            async () => {
                const counts = new Map<string, { [platform in Platform]?: { policies: number } }>();

                const policyLookup = await this.tagPolicySvc.getPolicyLookup();
                const platforms = [...policyLookup.getPlatforms()];
                for (const platform of platforms) {
                    for (const tag of tags) {
                        const policies = policyLookup.getPolicies(platform, tag)?.length ?? 0;
                        const stats = counts.get(tag) ?? counts.set(tag, {}).get(tag)!;
                        stats[platform] = { policies };
                    }
                }

                return { platforms, counts };
            },
            { platforms: [], counts: new Map() }
        );

    private getAutomationStats = (tags: string[]) =>
        this.safelyGetStats(async () => {
            const result = new Map<string, { rules: number }>(tags.map((t) => [t, { rules: 0 }]));

            const tagRules = await getTagAutomationGetTagAutomationRules();
            for (const [tag, rules] of this.tagRuleReader.getTagRuleCounts(tagRules)) {
                result.set(tag, { rules });
            }

            return result;
        }, new Map());
}
